/**
 * Admin Settings - Weekly Report Configuration with Scheduler Status
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Calendar, Clock, Send, CheckCircle, AlertCircle, Settings, Play, Image, RefreshCw, QrCode, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC (GMT+0)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'America/New_York', label: 'New York (EST)' },
  { value: 'America/Chicago', label: 'Chicago (CST)' },
  { value: 'America/Denver', label: 'Denver (MST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' }
];

export const AdminSettingsModal = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    weekly_report_enabled: false,
    weekly_report_email: '',
    weekly_report_day: 'monday',
    show_qr_on_upload: true,
    timezone: 'UTC'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [triggeringReport, setTriggeringReport] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [runningMigration, setRunningMigration] = useState(false);
  const [activeTab, setActiveTab] = useState('reports');

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/api/admin/settings`),
        axios.get(`${API}/api/admin/scheduler/status`)
      ]);
      setSettings({
        weekly_report_enabled: settingsRes.data.weekly_report_enabled || false,
        weekly_report_email: settingsRes.data.weekly_report_email || '',
        weekly_report_day: settingsRes.data.weekly_report_day || 'monday',
        show_qr_on_upload: settingsRes.data.show_qr_on_upload !== false, // default true
        timezone: settingsRes.data.timezone || 'UTC'
      });
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMigrationPreview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/migration/thumbnails/preview`);
      setMigrationPreview(response.data);
    } catch (error) {
      // Non-master admins can't access this
      setMigrationPreview(null);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      loadMigrationPreview();
    }
  }, [isOpen, loadSettings, loadMigrationPreview]);

  const handleSave = async () => {
    if (settings.weekly_report_enabled && !settings.weekly_report_email) {
      toast.error('Please enter an email address');
      return;
    }

    if (settings.weekly_report_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.weekly_report_email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/api/admin/settings`, settings);
      toast.success('Settings saved successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!settings.weekly_report_email) {
      toast.error('Please enter an email address first');
      return;
    }

    // Save settings first
    try {
      setSaving(true);
      await axios.post(`${API}/api/admin/settings`, settings);
    } catch (error) {
      toast.error('Failed to save settings');
      setSaving(false);
      return;
    }
    setSaving(false);

    // Send test email
    try {
      setSendingTest(true);
      const response = await axios.post(`${API}/api/admin/reports/test-email`);
      if (response.data.success) {
        toast.success(`Test email sent to ${settings.weekly_report_email}`);
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email. Check Resend API key configuration.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleTriggerReport = async () => {
    if (!settings.weekly_report_email) {
      toast.error('Please configure an email address first');
      return;
    }

    try {
      setTriggeringReport(true);
      const response = await axios.post(`${API}/api/admin/reports/trigger-now`);
      if (response.data.success) {
        toast.success(`Analytics report sent to ${settings.weekly_report_email}`);
      } else {
        toast.error(response.data.message || 'Failed to send report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger report');
    } finally {
      setTriggeringReport(false);
    }
  };

  const handleRunMigration = async () => {
    try {
      setRunningMigration(true);
      const response = await axios.post(`${API}/api/admin/migration/thumbnails/run`);
      toast.success(`Migration complete: ${response.data.thumbnails_created} thumbnails created`);
      setMigrationPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run migration');
    } finally {
      setRunningMigration(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-primary" />
              <div>
                <h2 className="text-xl font-semibold">Admin Settings</h2>
                <p className="text-sm text-muted-foreground">System configuration & maintenance</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
              data-testid="settings-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-border shrink-0 overflow-x-auto">
            {[
              { id: 'reports', label: 'Reports', icon: Mail },
              { id: 'display', label: 'Display', icon: Globe },
              { id: 'upload', label: 'Upload', icon: QrCode },
              { id: 'maintenance', label: 'Maintenance', icon: RefreshCw }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activeTab === 'reports' ? (
              <>
                {/* Scheduler Status */}
                {schedulerStatus && (
                  <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${schedulerStatus.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                      <div className="flex-1">
                        <p className="font-medium text-green-800 dark:text-green-200">
                          Scheduler {schedulerStatus.running ? 'Active' : 'Inactive'}
                        </p>
                        {schedulerStatus.jobs?.length > 0 && (
                          <p className="text-sm text-green-700 dark:text-green-300">
                            Next run: {new Date(schedulerStatus.jobs[0].next_run_time).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                )}

                {/* Enable Weekly Reports */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 border border-border">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium">Weekly Analytics Reports</p>
                      <p className="text-sm text-muted-foreground">Automatic email summaries (8 AM UTC)</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, weekly_report_enabled: !settings.weekly_report_enabled })}
                    className={`w-12 h-6 rounded-full transition-all ${
                      settings.weekly_report_enabled ? 'bg-primary' : 'bg-secondary'
                    }`}
                    data-testid="weekly-report-toggle"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                      settings.weekly_report_enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={settings.weekly_report_email}
                    onChange={(e) => setSettings({ ...settings, weekly_report_email: e.target.value })}
                    placeholder="admin@example.com"
                    className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="weekly-report-email"
                  />
                </div>

                {/* Day Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    Send Report On
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map((day) => (
                      <button
                        key={day.value}
                        onClick={() => setSettings({ ...settings, weekly_report_day: day.value })}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          settings.weekly_report_day === day.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary hover:bg-secondary/80'
                        }`}
                        data-testid={`day-${day.value}`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleSendTestEmail}
                    disabled={sendingTest || !settings.weekly_report_email}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-secondary hover:bg-secondary/80 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="send-test-email"
                  >
                    {sendingTest ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Test Email
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleTriggerReport}
                    disabled={triggeringReport || !settings.weekly_report_email}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="trigger-report-now"
                  >
                    {triggeringReport ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        Send Now
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : activeTab === 'display' ? (
              /* Display Settings Tab */
              <>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="font-medium">Display Settings</h3>
                      <p className="text-sm text-muted-foreground">
                        Configure how data is displayed in the dashboard
                      </p>
                    </div>
                  </div>

                  {/* Timezone Selector */}
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      Analytics Timezone
                    </label>
                    <select
                      value={settings.timezone}
                      onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                      className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                      data-testid="timezone-select"
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-2">
                      All analytics timestamps and charts will be displayed in this timezone.
                    </p>
                  </div>

                  {/* Current Time Preview */}
                  <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                    <p className="text-sm text-muted-foreground mb-1">Current time in selected timezone:</p>
                    <p className="text-lg font-mono font-medium">
                      {new Date().toLocaleString('en-GB', { 
                        timeZone: settings.timezone,
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </>
            ) : activeTab === 'upload' ? (
              /* Upload Page Tab */
              <>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <QrCode className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="font-medium">Upload Page Settings</h3>
                      <p className="text-sm text-muted-foreground">
                        Configure what users see on the public upload page
                      </p>
                    </div>
                  </div>

                  {/* QR Code Button Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="flex items-center gap-3">
                      <QrCode className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Show QR Code Button</p>
                        <p className="text-sm text-muted-foreground">
                          Allow users to download QR codes from the upload page
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSettings({ ...settings, show_qr_on_upload: !settings.show_qr_on_upload })}
                      className={`w-12 h-6 rounded-full transition-all ${
                        settings.show_qr_on_upload ? 'bg-primary' : 'bg-secondary'
                      }`}
                      data-testid="show-qr-toggle"
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                        settings.show_qr_on_upload ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <p className="text-xs text-muted-foreground mt-2">
                    When disabled, users will need to access QR codes through the admin dashboard only.
                  </p>
                </div>
              </>
            ) : (
              /* Maintenance Tab */
              <>
                {/* Thumbnail Migration */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Image className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="font-medium">Thumbnail Migration</h3>
                      <p className="text-sm text-muted-foreground">
                        Generate missing thumbnails for older images
                      </p>
                    </div>
                  </div>

                  {migrationPreview ? (
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Total Images</p>
                          <p className="text-xl font-bold">{migrationPreview.images_found || 0}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Missing Thumbnails</p>
                          <p className="text-xl font-bold text-amber-600">{migrationPreview.images_without_thumbnail || 0}</p>
                        </div>
                      </div>
                      
                      {migrationPreview.thumbnails_created > 0 && (
                        <div className="flex items-center gap-2 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          {migrationPreview.thumbnails_created} thumbnails created
                        </div>
                      )}

                      {(migrationPreview.images_without_thumbnail || 0) > 0 && (
                        <button
                          onClick={handleRunMigration}
                          disabled={runningMigration}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium transition-all disabled:opacity-50"
                          data-testid="run-migration"
                        >
                          {runningMigration ? (
                            <>
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Running Migration...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4" />
                              Generate Missing Thumbnails
                            </>
                          )}
                        </button>
                      )}

                      {(migrationPreview.images_without_thumbnail || 0) === 0 && (
                        <div className="flex items-center gap-2 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          All images have thumbnails
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border text-center text-sm text-muted-foreground">
                      Migration preview not available
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border flex justify-end gap-3 shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-full bg-secondary font-medium"
              data-testid="settings-cancel"
            >
              Cancel
            </button>
            {(activeTab === 'reports' || activeTab === 'display' || activeTab === 'upload') && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="settings-save"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AdminSettingsModal;
